import React from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChartLine } from '@fortawesome/free-solid-svg-icons';
import { useQuery } from '@tanstack/react-query';
import FinancialService from '../../../AppSettings/services/FinancialService';
import VenExpService from '../../../AppSettings/services/VenExpService';
import OppExpService from '../../../AppSettings/services/OppExpService';
import GenExpensesService from '../../../AppSettings/services/GenExpensesService';
import { normalizeToArray } from '../../../helpers/responseHelpers';
import { computeBilledTimeAmount } from '../../../helpers/billingHelpers';
import FixBillService from '../../../AppSettings/services/FixBillService';
import SectionCard from '../../../Components/SectionCard';
import './ForecastActualCard.css';

interface FinancialTotals {
  billableTime: number;
  fixedFees: number;
  other: number;
  vendor: number;
  operating: number;
  general: number;
  fixedBills: number;
}

const ForecastActualCard: React.FC = () => {
  const { data: totals, isLoading, error } = useQuery<FinancialTotals>({
    queryKey: ['dashboard-financial-totals'],
    queryFn: async () => {
      const [
        billedTimeData,
        fixedFeesData,
        otherBillingData,
        vendorData,
        operatingData,
        generalData,
        fixedBillsData,
      ] = await Promise.all([
        FinancialService.getAllBilledTime(),
        FinancialService.getAllAssignedFinancialData(),
        FinancialService.getAllOtherBilling(),
        VenExpService.getAllVenExpenses(),
        OppExpService.getAllOppExpenses(),
        GenExpensesService.getAllGenExpenses(),
        FixBillService.getAllFixBillExpenses(),
      ]);

      const sum = (arr: any[]) =>
        arr.reduce((s: number, i: any) => s + (Number.isFinite(parseFloat(i.amount)) ? parseFloat(i.amount) : 0), 0);

      const billedTimeSum = (arr: any[]) =>
        arr.reduce((s: number, i: any) => s + computeBilledTimeAmount(i), 0);

      const fixedFeesList = normalizeToArray(fixedFeesData as any);
      const fixedFeesSum = fixedFeesList.reduce(
        (s: number, m: any) =>
          s + ((m.fixed_fees || []).reduce((ms: number, f: any) => ms + (Number.isFinite(parseFloat(f.fees)) ? parseFloat(f.fees) : 0), 0)),
        0,
      );

      return {
        billableTime: billedTimeSum(normalizeToArray(billedTimeData)),
        fixedFees: fixedFeesSum,
        other: sum(normalizeToArray(otherBillingData)),
        vendor: sum(normalizeToArray(vendorData)),
        operating: sum(normalizeToArray(operatingData)),
        general: sum(normalizeToArray(generalData)),
        fixedBills: sum(normalizeToArray(fixedBillsData)),
      };
    },
    staleTime: 2 * 60 * 1000,
  });

  const formatCurrency = (amount: number): string =>
    new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(amount);

  const forecastProfits = (totals?.billableTime ?? 0) + (totals?.fixedFees ?? 0) + (totals?.other ?? 0);
  const forecastCosts = (totals?.vendor ?? 0) + (totals?.operating ?? 0) + (totals?.general ?? 0) + (totals?.fixedBills ?? 0);
  const forecastTotal = forecastProfits + forecastCosts;

  const actualProfits = 0;
  const actualCosts = 0;
  const actualTotal = actualProfits + actualCosts;

  const iconElement = (
    <div className="forecastActualCard-icon-container">
      <FontAwesomeIcon icon={faChartLine} className="forecastActualCard-icon" />
    </div>
  );

  if (isLoading) {
    return (
      <SectionCard title="Forecast vs Actual" right={iconElement}>
        <div>Loading...</div>
      </SectionCard>
    );
  }

  if (error) {
    return (
      <SectionCard title="Forecast vs Actual" right={iconElement}>
        <div>Error: {(error as Error).message}</div>
      </SectionCard>
    );
  }

  return (
    <SectionCard 
      title="Forecast vs Actual" 
      right={iconElement}
      className="forecastActualCard-container"
    >
      <div className="forecastActualCard-content">
        <div className="forecastActualCard-section">
          <div className="forecastActualCard-section-title">Forecast</div>
          <div className="forecastActualCard-status-item">
            <div className="forecastActualCard-status-left">
              <span className="forecastActualCard-dot forecastActualCard-dot--forecast"></span>
              <span className="forecastActualCard-label">Total</span>
            </div>
            <span className="forecastActualCard-amount">{formatCurrency(forecastTotal)}</span>
          </div>
          <div className="forecastActualCard-status-item">
            <div className="forecastActualCard-status-left">
              <span className="forecastActualCard-dot forecastActualCard-dot--forecast"></span>
              <span className="forecastActualCard-label">Profits</span>
            </div>
            <span className="forecastActualCard-amount">{formatCurrency(forecastProfits)}</span>
          </div>
          <div className="forecastActualCard-status-item">
            <div className="forecastActualCard-status-left">
              <span className="forecastActualCard-dot forecastActualCard-dot--forecast"></span>
              <span className="forecastActualCard-label">Costs</span>
            </div>
            <span className="forecastActualCard-amount">{formatCurrency(forecastCosts)}</span>
          </div>
        </div>

        <div className="forecastActualCard-divider"></div>

        <div className="forecastActualCard-section">
          <div className="forecastActualCard-section-title">Actual</div>
          <div className="forecastActualCard-status-item">
            <div className="forecastActualCard-status-left">
              <span className="forecastActualCard-dot forecastActualCard-dot--actual"></span>
              <span className="forecastActualCard-label">Total</span>
            </div>
            <span className="forecastActualCard-amount">{formatCurrency(actualTotal)}</span>
          </div>
          <div className="forecastActualCard-status-item">
            <div className="forecastActualCard-status-left">
              <span className="forecastActualCard-dot forecastActualCard-dot--actual"></span>
              <span className="forecastActualCard-label">Profits</span>
            </div>
            <span className="forecastActualCard-amount">{formatCurrency(actualProfits)}</span>
          </div>
          <div className="forecastActualCard-status-item">
            <div className="forecastActualCard-status-left">
              <span className="forecastActualCard-dot forecastActualCard-dot--actual"></span>
              <span className="forecastActualCard-label">Costs</span>
            </div>
            <span className="forecastActualCard-amount">{formatCurrency(actualCosts)}</span>
          </div>
        </div>
      </div>
    </SectionCard>
  );
};

export default ForecastActualCard;
